<script>
  import { onMount, onDestroy } from "svelte";
  import { lang } from '../lib/stores';
  import { scaleLinear } from "d3-scale";
  import { select } from "d3-selection";
  import { link, curveBumpX } from "d3-shape";
  import { extent } from "d3";
  import { createId, generateId } from "../lib/functions";

  export let data;
  export let show = "length";

  let selectedLang;
	const unsubscribe = lang.subscribe((value) => selectedLang = value);

  const marginsSmall = [2, 20, 2, 2];
  const margins = [22, 2, 2, 22];
  const smallChartHeight = 18;
  const smallTextHeight = 10;

  const termHeight = 45;
  const albumHeight = 80;
  const songHeight = 25;
  const albumSpacer = 25;
  const distance = 10;
  const componentId = generateId();

  let expanded = false;
  let svgObject;
  let width = 0;
  let svgWidth = 0;
  let svgHeight = 0;
  let smallScreen = "";
  let isLarge = false;
  let smallXScale;

  const selectPoint = (id, type) => {
    const selected = svgObject.querySelectorAll(`.selected`);
    selected.forEach((s) => s.classList.remove("selected"));
    const selectedItems = document.querySelectorAll(`[data-${type}~="${id}"]`);
    selectedItems.forEach((s) => s.classList.add("selected"));
  };

  const expand = () => {
    expanded = !expanded;
  };

  const draw = () => {
    isLarge = window.matchMedia("screen and (min-width: 768px").matches;
    if (data != null) {
      // Reset
      svgWidth = width;
      svgHeight = 0;
      smallScreen = "";
      if (isLarge) {
        // Reset
        if (svgObject != null) {
          const svg = select(svgObject);
          svg.selectAll("g").remove();

          const xPosTerms = (svgWidth - margins[1] - margins[2]) / 3;
          const xPosSongs = xPosTerms * 2;
          const dataExtent = extent(data?.terms, (d) => d.count);
          dataExtent[0] = 0;
          const x = scaleLinear().range([0, xPosTerms]).domain(dataExtent);

          const songsHeight =
            data?.albumsLarge?.reduce(
              (acc, a) => acc + albumHeight + songHeight * a.songs.length,
              0,
            ) +
            albumSpacer * ((data?.albumsLarge.length ?? 0) - 1);
          const termsHeight = data?.terms.length * termHeight;
          svgHeight =
            margins[0] + margins[2] + Math.max(songsHeight, termsHeight);

          const baseYTerms =
            (svgHeight - margins[0] - margins[2]) / 2 - termsHeight / 2;
          const baseYSongs =
            (svgHeight - margins[0] - margins[2]) / 2 - songsHeight / 2;

          const g = svg
            .append("g")
            .attr("transform", `translate(${margins[3]}, ${margins[0]})`);

          const gl = g.append("g");

          const gs = g
            .append("g")
            .attr("class", "linksToSongs-songs-list")
            .attr("transform", `translate(0, ${baseYSongs})`);

          const gt = g
            .append("g")
            .attr("class", "linksToSongs-term-list")
            .attr("transform", `translate(0, ${baseYTerms})`);

          const map = {};
          const linkGenerator = link(curveBumpX);

          let prevY = 0;
          data?.albumsLarge.forEach((a) => {
            gs.append("image")
              .attr("x", xPosSongs)
              .attr("y", prevY)
              .attr("width", 50)
              .attr("height", 50)
              .attr("class", "linksToSongs-albums-cover")
              .attr("href", a.cover);
            prevY += albumHeight;
            a.songs.forEach((s) => {
              const songId = `${createId(s.title)}`;
              gs.append("text")
                .attr("x", xPosSongs)
                .attr("y", prevY)
                .attr("dominant-baseline", "middle")
                .attr("text-anchor", "start")
                .attr("class", `linksToSongs-song-label ${s.id} ${s.terms}`)
                .attr("fill", "black")
                .attr("data-terms", s.terms)
                .attr("data-songs", s.id)
                .style("fill", "var(--foreground")
                .text(s.title)
                .on("click", () => selectPoint(songId, "songs"));
              map[songId] = { y: prevY };
              prevY += songHeight;
            });
            prevY += albumSpacer;
          });

          data?.terms.forEach((d, i) => {
            const yPos = i * termHeight;
            const termId = createId(d.term);
            const songsIds = d.songs.map((s) => `${createId(s)}`).join(" ");
            gt.append("text")
              .attr("x", xPosTerms)
              .attr("y", yPos)
              .attr("dominant-baseline", "middle")
              .attr("text-anchor", "end")
              .attr(
                "class",
                `linksToSongs-term-label ${songsIds} ${`${createId(d.term)}`}`,
              )
              .attr("fill", "black")
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("fill", "var(--foreground)")
              .text(d.term)
              .on("click", () => selectPoint(termId, "terms"));

            d.songs.forEach((s) => {
              const songId = createId(s);
              gl.append("path")
                .attr(
                  "d",
                  linkGenerator({
                    source: [xPosTerms + distance, yPos + baseYTerms],
                    target: [xPosSongs - distance, map[songId].y + baseYSongs],
                  }),
                )
                .attr("fill", "none")
                .attr("stroke", "black")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "1 4")
                .attr("class", `linksToSongs-connector ${songId} ${termId}`)
                .attr("data-terms", termId)
                .attr("data-songs", songId)
                .style("stroke", "var(--grey-lightest)");
            });

            gt.append("line")
              .attr("x1", xPosTerms)
              .attr("y1", yPos + 14)
              .attr("x2", xPosTerms - x(dataExtent[1]))
              .attr("y2", yPos + 14)
              .attr("stroke", "black")
              .attr("stroke-width", 4)
              .attr(
                "class",
                `data linksToSongs-ngram-shadow ${songsIds} ${`${termId}`}`,
              )
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("stroke", "var(--grey-dirtywhite)");

            gt.append("line")
              .attr("x1", xPosTerms)
              .attr("y1", yPos + 14)
              .attr("x2", xPosTerms - x(d.count))
              .attr("y2", yPos + 14)
              .attr("stroke", "black")
              .attr("stroke-width", 4)
              .attr(
                "class",
                `data linksToSongs-ngram-bar ${songsIds} ${`${termId}`}`,
              )
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("stroke", "transparent");

            gt.append("line")
              .attr("x1", xPosTerms)
              .attr("y1", yPos + 14)
              .attr("x2", xPosTerms)
              .attr("y2", yPos + 20)
              .attr("stroke", "black")
              .attr("stroke-width", 1)
              .attr(
                "class",
                `data linksToSongs-ngram-bar-tick ${songsIds} ${`${termId}`}`,
              )
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("stroke", "transparent");

            gt.append("line")
              .attr("x1", xPosTerms - x(d.count))
              .attr("y1", yPos + 14)
              .attr("x2", xPosTerms - x(d.count))
              .attr("y2", yPos + 20)
              .attr("stroke", "black")
              .attr("stroke-width", 1)
              .attr(
                "class",
                `data linksToSongs-ngram-bar-tick ${songsIds} ${`${termId}`}`,
              )
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("stroke", "transparent");

            gt.append("text")
              .attr("x", xPosTerms - x(d.count))
              .attr("y", yPos + 14)
              .attr("dy", 8)
              .attr("dominant-baseline", "hanging")
              .attr("text-anchor", "start")
              .attr(
                "class",
                `data linksToSongs-ngram-bar-label ${songsIds} ${termId}`,
              )
              .attr("fill", "black")
              .attr("data-terms", termId)
              .attr("data-songs", songsIds)
              .style("fill", "transparent")
              .text((() => {
                if (selectedLang === 'it') {
                  return `${d.count} ${d.count > 1 ? "ripetizioni" : "ripetizione"}`;
                } else {
                  return `${d.count} ${d.count > 1 ? "occurrences" : "occurrence"}`;
                }
              })());
          });
          selectPoint(createId(data?.terms[0].term), "terms");
        }
      } else {
        let dataExtent = [];
        switch (show) {
          case "both":
            const dataExtentC = extent(data.terms, (d) => d.count);
            const dataExtentL = extent(data.terms, (d) => d.songs.length);
            dataExtent[1] = Math.max(dataExtentC[1], dataExtentL[1]);
            break;
          case "count":
            dataExtent = extent(data.terms, (d) => d.count);
            break;
          default:
            dataExtent = extent(data.terms, (d) => d.songs.length);
        }
        dataExtent[0] = 0;
        if (!dataExtent[1] % 10 === 0) {
          dataExtent[1] = Math.ceil(dataExtent[1] / 10) * 10;
        }
        smallXScale = scaleLinear()
          .range([0, svgWidth - marginsSmall[1] - marginsSmall[3]])
          .domain(dataExtent);
        svgHeight = smallChartHeight + marginsSmall[0] + marginsSmall[2];
      }
    }
  };

  onDestroy(unsubscribe);
  onMount(() => draw());

  $: data, draw();
  $: width, draw();
</script>

<section bind:clientWidth={width} class="linksToSongs" id={componentId}>
  {#if isLarge}
    <div class="linksToSongs-large">
      <svg
        bind:this={svgObject}
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        class="chart-svg-linksToSongs"
      />
    </div>
  {:else}
    <div class="linksToSongs-small">
      <div class="linksToSongs-small-wrapper">
        {#each data.terms as d, index}
          {#if index < 4 || expanded}
            <div class="linksToSongs-item" id={createId(d.term)}>
              <h3 class="linksToSongs-title">{d.term}</h3>
              <div class="linksToSongs-chart">
                {#if show === "count" || show === "both"}
                  {#if index === 0}
                    <svg
                      width={svgWidth}
                      height={smallTextHeight}
                      viewBox="0 0 {svgWidth} {smallTextHeight}"
                    >
                      <circle
                        cx="7"
                        cy={smallTextHeight / 2}
                        r="3"
                        stroke="blak"
                        stroke-width="1"
                        fill="black"
                        style="fill: url(#dots-accent-2); stroke: var(--accent-2)"
                      />
                      <text
                        x="0"
                        dx="15"
                        y={smallTextHeight / 2}
                        fill="black"
                        dominant-baseline="middle"
                        style="var(--foreground)"
                        class="data linksToSongs-small-legend"
                        >Numero di Ripetizioni</text
                      >
                    </svg>
                  {/if}
                  <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox="0 0 {svgWidth} {svgHeight}"
                  >
                    <rect
                      x={marginsSmall[3]}
                      y={marginsSmall[0]}
                      width={smallXScale(d.count)}
                      height={smallChartHeight}
                      fill="black"
                      stroke="black"
                      stroke-width="1"
                      style="fill: url(#dots-accent-2); stroke: var(--accent-2);"
                    />
                    <text
                      x={smallXScale(d.count)}
                      dx="5"
                      y={svgHeight / 2}
                      dominant-baseline="middle"
                      text-achor="start"
                      class="ngramsSongs-small-chartLabel"
                      fill="black"
                      style="fill: var(--accent-2)"
                      >{new Intl.NumberFormat("it-IT").format(d.count)}</text
                    >
                  </svg>
                {/if}
                {#if show === "length" || show === "both"}
                  {#if index === 0}
                    <svg
                      width={svgWidth}
                      height={smallTextHeight}
                      viewBox="0 0 {svgWidth} {smallTextHeight}"
                    >
                      <circle
                        cx="7"
                        cy={smallTextHeight / 2}
                        r="3"
                        stroke="blak"
                        stroke-width="1"
                        fill="black"
                        style="fill: url(#dots-accent-1); stroke: var(--accent-1d)"
                      />
                      <text
                        x="0"
                        dx="15"
                        y={smallTextHeight / 2}
                        fill="black"
                        dominant-baseline="middle"
                        style="var(--foreground)"
                        class="data linksToSongs-small-legend"
                        >Numero di Brani</text
                      >
                    </svg>
                  {/if}
                  <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox="0 0 {svgWidth} {svgHeight}"
                  >
                    <rect
                      x={marginsSmall[3]}
                      y={marginsSmall[0]}
                      width={smallXScale(d.songs.length)}
                      height={smallChartHeight}
                      fill="black"
                      stroke="black"
                      stroke-width="1"
                      style="fill: url(#dots-accent-1); stroke: var(--accent-1d);"
                    />
                    <text
                      x={smallXScale(d.songs.length)}
                      dx="5"
                      y={svgHeight / 2}
                      dominant-baseline="middle"
                      text-achor="start"
                      class="ngramsSongs-small-chartLabel"
                      fill="black"
                      style="fill: var(--accent-1d)"
                      >{new Intl.NumberFormat("it-IT").format(
                        d.songs.length,
                      )}</text
                    >
                  </svg>
                {/if}
              </div>
              <div class="linksToSongs-small-albums">
                {#each data?.albumsSmall[createId(d.term)].albums as a}
                  {#if a.songs.length > 0}
                    <div class="linksToSongs-small-album">
                      <div class="cover">
                        <img
                          loading="lazy"
                          src={a.cover}
                          alt={a.title}
                          width="50"
                          height="50"
                        />
                      </div>
                      <div class="text">
                        <h4>{a.title} ({a.year})</h4>
                        <p>
                          {#if a.songs.length > 1}Nei brani{:else}Nel brano{/if}:
                          {#each a.songs as s, indexS}
                            <em>
                              {s.title}
                            </em>{#if indexS < a.songs.length - 1},{"  "}{:else}.{/if}
                          {/each}
                        </p>
                      </div>
                    </div>
                  {/if}
                {/each}
              </div>
            </div>
          {/if}
        {/each}
      </div>
      <div class="linksToSong-button-wrapper">
        <button on:click={expand} class="button"
          >{#if expanded}{#if selectedLang === 'it'}Nascondi{:else}Hide{/if}{:else}{#if selectedLang === 'it'}Mostra tutti{:else}Show All{/if}{/if}</button
        >
      </div>
    </div>
  {/if}
</section>

<style>
  .linksToSongs {
    display: block;
  }

  .linksToSongs-small-legend {
    font-size: 9px;
    text-transform: uppercase;
  }

  .linksToSongs-title {
    color: var(--accent-2);
    text-transform: uppercase;
  }

  .linksToSongs-small-album {
    display: flex;
    margin-bottom: 2rem;
  }

  .linksToSongs-small-album img {
    /* border-radius: 50%; */
  }

  .linksToSongs-small-album .text {
    padding-left: 1rem;
  }

  .linksToSongs-small-album .text h4 {
    font-size: 12px;
  }

  .linksToSongs-small .linksToSongs-chart {
    margin-bottom: 1rem;
  }

  .linksToSong-button-wrapper {
    text-align: center;
  }

  .linksToSong-button-wrapper .button {
    background: transparent;
    border: 1px solid var(--accent-2);
    color: var(--accent-2);
    font-family: var(--title-font-family);
    font-style: var(--title-font-style);
    font-weight: var(--title-font-weight);
    height: 3rem;
    line-height: 3rem;
    padding: 0 2rem;
  }
</style>
